/**
 * Owner/beheerder management (Deel A, expanded): every registered user, with
 * their role, a display name the owner (or the user themselves, in Account)
 * can set, and -- owner only -- a role change or account deletion. Also who
 * may become a beheerder (implicit: anyone in this list), and inviting new
 * colleagues into the project (owner or beheerder -- moved here from
 * ServerSettings' ServerInfo).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownMenu } from 'radix-ui';
import {
  ArrowRightLeft,
  Check,
  Copy,
  Crown,
  MoreHorizontal,
  Pencil,
  QrCode,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import { ConfirmDialog } from '../components/Dialog';
import { cn } from '../lib/cn';
import { supabase, useSyncState } from './useSync';
import { linkTarget } from './config';
import { currentConnection, inviteLink } from './connect';
import { Qr } from './ServerSettings';
import { useRole } from './useRole';

const field = 'w-full rounded-lg border border-border bg-bg px-2 py-1.5 text-sm';
const secondary =
  'flex items-center gap-1.5 rounded-lg border border-border px-2 py-1 text-sm hover:bg-bg-hover disabled:opacity-50';
const menu =
  'z-[70] flex min-w-40 flex-col rounded-xl border border-border bg-bg p-1 text-sm shadow-pop';
const menuItem =
  'flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-left outline-none hover:bg-bg-hover data-[highlighted]:bg-bg-hover';

type UserStatus = 'active' | 'invited' | 'disabled';

interface UserRow {
  userId: string;
  email: string | null;
  displayName: string | null;
  role: 'owner' | 'beheerder' | null;
  status: UserStatus;
  createdAt: string;
}

const STATUS_DOT: Record<UserStatus, string> = {
  active: 'bg-green-500',
  invited: 'bg-amber-500',
  disabled: 'bg-red-500',
};

function StatusBadge({ status }: { status: UserStatus }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-1.5 py-px text-xs text-fg-muted">
      <span className={cn('size-1.5 shrink-0 rounded-full', STATUS_DOT[status])} />
      {t(`management.status.${status}`)}
    </span>
  );
}

export function ManagementSettings() {
  const role = useRole();

  if (role === null) return null;

  return (
    <div className="flex flex-col gap-4 text-sm">
      <UsersList isOwner={role === 'owner'} />
      <ServerInvite />
      <InviteColleagueForm />
      <InviteLog />
      <WorkspacesList />
    </div>
  );
}

/** Link/QR to connect a colleague's app to this server -- no account, no
 * password, just points them at the right project. An actual account comes
 * from InviteColleagueForm below. Moved here from ServerSettings' ServerInfo:
 * it's an owner/beheerder call, same as everything else in Beheer. */
function ServerInvite() {
  const { t } = useTranslation();
  const conn = currentConnection();
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!conn) return null;
  const link = inviteLink(conn, conn.webUrl ?? '');

  return (
    <div className="flex flex-col gap-2">
      <span className="font-medium">{t('server.inviteLinkTitle')}</span>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={secondary}
          onClick={() =>
            void navigator.clipboard.writeText(link).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            })
          }
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          {copied ? t('server.copied') : t('server.copyInvite')}
        </button>
        <button
          type="button"
          className={secondary}
          aria-expanded={showQr}
          onClick={() => setShowQr((v) => !v)}
        >
          <QrCode className="size-3.5" />
          {t('server.qr')}
        </button>
      </div>
      <p className="text-xs text-fg-muted">
        {t(conn.webUrl ? 'server.inviteHintLink' : 'server.inviteHintCode')}
      </p>
      {showQr && <Qr text={link} label={t('server.qr')} />}
    </div>
  );
}

function UsersList({ isOwner }: { isOwner: boolean }) {
  const { t } = useTranslation();
  const ownUserId = useSyncState((s) => s.session?.user.id);
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<UserRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = () =>
    void supabase()
      .functions.invoke<{ users?: UserRow[] }>('manage-admins', { body: { action: 'list' } })
      .then(({ data }) => setUsers(data?.users ?? []));

  useEffect(() => {
    load();
  }, []);

  const call = async (body: Record<string, unknown>) => {
    const { error: err } = await supabase().functions.invoke('manage-admins', { body });
    if (err) throw err;
  };

  const setRole = async (u: UserRow, role: 'beheerder' | 'user') => {
    setBusy(u.userId);
    setError(null);
    try {
      await call({ action: 'setRole', userId: u.userId, role });
      load();
    } catch {
      setError(t('management.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  const setStatus = async (u: UserRow, status: 'active' | 'disabled') => {
    setBusy(u.userId);
    setError(null);
    try {
      await call({ action: 'setStatus', userId: u.userId, status });
      load();
    } catch {
      setError(t('management.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  const setName = async (u: UserRow, name: string) => {
    setEditing(null);
    if (name === (u.displayName ?? '')) return;
    setBusy(u.userId);
    setError(null);
    try {
      await call({ action: 'setName', userId: u.userId, name });
      load();
    } catch {
      setError(t('management.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  const deleteUser = async (u: UserRow) => {
    setBusy(u.userId);
    setDeleteError(null);
    try {
      await call({ action: 'delete', userId: u.userId });
      setConfirmDelete(null);
      load();
    } catch (e) {
      const ctx = (e as { context?: Response }).context;
      const code = ctx ? ((await ctx.json().catch(() => ({}))) as { error?: string }).error : '';
      setDeleteError(
        t(
          code === 'has-workspaces'
            ? 'management.deleteBlockedWorkspaces'
            : code === 'has-pages'
              ? 'management.deleteBlockedPages'
              : 'management.actionFailed',
        ),
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="font-medium">{t('management.usersTitle')}</span>
      {users === null && <p className="text-xs text-fg-muted">{t('management.loading')}</p>}
      {users?.map((u) => {
        const self = u.userId === ownUserId;
        const canManage = isOwner && !self && u.role !== 'owner';
        const canEdit = isOwner || u.role === null;
        const isEditing = editing === u.userId;
        return (
          <div key={u.userId} className="flex items-center gap-2">
            <span className="flex min-w-0 flex-[2] items-center gap-1.5">
              {u.role === 'owner' ? (
                <Crown className="size-3.5 shrink-0 text-accent" />
              ) : u.role === 'beheerder' ? (
                <ShieldCheck className="size-3.5 shrink-0 text-fg-muted" />
              ) : (
                <span className="size-3.5 shrink-0" />
              )}
              {isEditing ? (
                <input
                  autoFocus
                  defaultValue={u.displayName ?? ''}
                  aria-label={t('management.nameLabel')}
                  placeholder={u.email ?? ''}
                  onBlur={(e) => void setName(u, e.target.value.trim())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                    if (e.key === 'Escape') setEditing(null);
                  }}
                  className="min-w-0 flex-1 rounded-md border border-border bg-bg px-1.5 py-0.5 text-sm"
                />
              ) : (
                <span className="min-w-0 truncate">
                  {u.displayName ? (
                    <>
                      {u.displayName}
                      <span className="ml-1.5 truncate text-xs text-fg-muted">{u.email}</span>
                    </>
                  ) : (
                    (u.email ?? u.userId)
                  )}
                  {self && ` (${t('workspaces.you')})`}
                </span>
              )}
            </span>
            <span className="w-28 shrink-0">
              {isEditing && canManage ? (
                <select
                  value={u.status === 'disabled' ? 'disabled' : 'active'}
                  disabled={busy === u.userId}
                  onChange={(e) => void setStatus(u, e.target.value as 'active' | 'disabled')}
                  className="w-full rounded-lg border border-border bg-bg px-1.5 py-1 text-xs"
                >
                  <option value="active">{t('management.status.active')}</option>
                  <option value="disabled">{t('management.status.disabled')}</option>
                </select>
              ) : (
                <StatusBadge status={u.status} />
              )}
            </span>
            <span className="w-24 shrink-0">
              {isEditing && canManage ? (
                <select
                  value={u.role === 'beheerder' ? 'beheerder' : 'user'}
                  disabled={busy === u.userId}
                  onChange={(e) => void setRole(u, e.target.value as 'beheerder' | 'user')}
                  className="w-full rounded-lg border border-border bg-bg px-1.5 py-1 text-xs"
                >
                  <option value="user">{t('management.roleUser')}</option>
                  <option value="beheerder">{t('management.roleBeheerder')}</option>
                </select>
              ) : (
                u.role !== 'owner' && (
                  <span className="text-xs text-fg-muted">
                    {u.role === 'beheerder'
                      ? t('management.roleBeheerder')
                      : t('management.roleUser')}
                  </span>
                )
              )}
            </span>
            {(canEdit || canManage) && (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button
                    type="button"
                    title={t('management.options')}
                    aria-label={t('management.options')}
                    disabled={busy === u.userId}
                    className="grid size-6 shrink-0 place-items-center rounded-md text-fg-muted hover:bg-bg-hover hover:text-fg"
                  >
                    <MoreHorizontal className="size-3.5" />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content align="end" sideOffset={4} className={menu}>
                    {canEdit && (
                      <DropdownMenu.Item
                        className={menuItem}
                        onSelect={() => setEditing(isEditing ? null : u.userId)}
                      >
                        <Pencil className="size-3.5" />
                        {isEditing ? t('management.done') : t('management.editUser')}
                      </DropdownMenu.Item>
                    )}
                    {canManage && (
                      <DropdownMenu.Item
                        className={cn(menuItem, 'text-danger')}
                        onSelect={() => {
                          setDeleteError(null);
                          setConfirmDelete(u);
                        }}
                      >
                        <Trash2 className="size-3.5" />
                        {t('management.deleteUser')}
                      </DropdownMenu.Item>
                    )}
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            )}
          </div>
        );
      })}

      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={t('management.deleteConfirmTitle')}
        description={
          deleteError ??
          t('management.deleteConfirmBody', {
            name: confirmDelete?.displayName ?? confirmDelete?.email ?? '',
          })
        }
        confirmLabel={t('management.deleteUser')}
        cancelLabel={t('app.cancel')}
        onConfirm={() => confirmDelete && void deleteUser(confirmDelete)}
      />
    </div>
  );
}

/** Invite a colleague onto the project (moved from ServerSettings' ServerInfo). */
function InviteColleagueForm() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ error: boolean; text: string } | null>(null);

  return (
    <form
      aria-label={t('server.inviteTitle')}
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        setNote(null);
        const webUrl = linkTarget();
        void supabase()
          .functions.invoke<{ ok?: boolean; error?: string }>('invite', {
            body: { email: email.trim(), ...(webUrl ? { redirectTo: webUrl } : {}) },
          })
          .then(async ({ error }) => {
            if (!error) {
              setNote({ error: false, text: t('server.invited', { email: email.trim() }) });
              setEmail('');
              return;
            }
            const ctx = (error as { context?: Response }).context;
            const code = ctx
              ? ((await ctx.json().catch(() => ({}))) as { error?: string }).error
              : '';
            setNote({
              error: true,
              text: t(code === 'exists' ? 'server.inviteExists' : 'server.inviteFailed'),
            });
          })
          .finally(() => setBusy(false));
      }}
    >
      <span className="font-medium">{t('server.inviteTitle')}</span>
      <div className="flex gap-2">
        <input
          type="email"
          required
          aria-label={t('sync.email')}
          placeholder={t('sync.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={field}
        />
        <button type="submit" disabled={busy} className={cn(secondary, 'shrink-0')}>
          <UserPlus className="size-3.5" />
          {t('server.sendInvite')}
        </button>
      </div>
      {note && (
        <p
          role={note.error ? 'alert' : 'status'}
          className={cn('text-xs', note.error ? 'text-danger' : 'text-fg-muted')}
        >
          {note.text}
        </p>
      )}
    </form>
  );
}

/** Recent invites, who sent them (table readable to any team admin). */
function InviteLog() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<{ invitedEmail: string; createdAt: string }[] | null>(null);
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' });

  useEffect(() => {
    void supabase()
      .from('invite_log')
      .select('invited_email, created_at')
      .order('created_at', { ascending: false })
      .limit(20)
      .then(({ data }) =>
        setRows(
          (data ?? []).map((r) => ({
            invitedEmail: r.invited_email as string,
            createdAt: r.created_at as string,
          })),
        ),
      );
  }, []);

  if (!rows?.length) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium">{t('management.inviteLogTitle')}</span>
      <ul className="flex flex-col gap-0.5 text-xs text-fg-muted">
        {rows.map((r, i) => (
          <li key={i}>
            {t('management.inviteLogEntry', {
              email: r.invitedEmail,
              date: fmt.format(new Date(r.createdAt)),
            })}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface WorkspaceRow {
  id: string;
  name: string;
  createdBy: string;
  ownerEmail: string | null;
  ownerName: string | null;
  memberCount: number;
  status: 'active' | 'empty';
  createdAt: string;
}

/** Every workspace in the system (owner and beheerder both): rename, transfer ownership, delete. */
function WorkspacesList() {
  const { t, i18n } = useTranslation();
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short' });
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [transferring, setTransferring] = useState<string | null>(null);
  const [transferEmail, setTransferEmail] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<WorkspaceRow | null>(null);

  const load = () =>
    void supabase()
      .functions.invoke<{ workspaces?: WorkspaceRow[] }>('workspace-members', {
        body: { action: 'list-all' },
      })
      .then(({ data }) => setWorkspaces(data?.workspaces ?? []));

  useEffect(() => {
    load();
  }, []);

  const call = async (body: Record<string, unknown>) => {
    const { error: err } = await supabase().functions.invoke('workspace-members', { body });
    if (err) throw err;
  };

  const rename = async (w: WorkspaceRow, name: string) => {
    setEditing(null);
    if (!name || name === w.name) return;
    setBusy(w.id);
    setError(null);
    try {
      await call({ action: 'rename', workspaceId: w.id, name });
      load();
    } catch {
      setError(t('management.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  const transferOwner = async (w: WorkspaceRow) => {
    const email = transferEmail.trim();
    if (!email) return;
    setBusy(w.id);
    setError(null);
    try {
      await call({ action: 'transfer-owner', workspaceId: w.id, email });
      setTransferring(null);
      setTransferEmail('');
      load();
    } catch {
      setError(t('management.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  const deleteWorkspace = async (w: WorkspaceRow) => {
    setBusy(w.id);
    setError(null);
    try {
      await call({ action: 'delete-workspace', workspaceId: w.id });
      setConfirmDelete(null);
      load();
    } catch {
      setError(t('management.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="font-medium">{t('management.workspaces.title')}</span>
      {workspaces === null && <p className="text-xs text-fg-muted">{t('management.loading')}</p>}
      {workspaces?.map((w) => {
        const isEditing = editing === w.id;
        const isTransferring = transferring === w.id;
        return (
          <div key={w.id} className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <span className="flex min-w-0 flex-[2] items-center gap-1.5">
                {isEditing ? (
                  <input
                    autoFocus
                    defaultValue={w.name}
                    aria-label={t('management.workspaces.nameLabel')}
                    onBlur={(e) => void rename(w, e.target.value.trim())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                      if (e.key === 'Escape') setEditing(null);
                    }}
                    className="min-w-0 flex-1 rounded-md border border-border bg-bg px-1.5 py-0.5 text-sm"
                  />
                ) : (
                  <span className="min-w-0 truncate">
                    {w.name}
                    <span className="ml-1.5 truncate text-xs text-fg-muted">
                      {w.ownerName ?? w.ownerEmail}
                    </span>
                  </span>
                )}
              </span>
              <span className="inline-flex w-16 shrink-0 items-center gap-1.5 whitespace-nowrap text-xs text-fg-muted">
                <span
                  className={cn(
                    'size-1.5 shrink-0 rounded-full',
                    w.status === 'active' ? 'bg-green-500' : 'bg-fg-muted',
                  )}
                />
                {t(`management.workspaces.status.${w.status}`)}
              </span>
              <span className="flex w-14 shrink-0 items-center gap-1 text-xs text-fg-muted">
                <Users className="size-3 shrink-0" />
                {w.memberCount}
              </span>
              <span className="w-20 shrink-0 text-xs text-fg-muted">
                {fmt.format(new Date(w.createdAt))}
              </span>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button
                    type="button"
                    title={t('management.options')}
                    aria-label={t('management.options')}
                    disabled={busy === w.id}
                    className="grid size-6 shrink-0 place-items-center rounded-md text-fg-muted hover:bg-bg-hover hover:text-fg"
                  >
                    <MoreHorizontal className="size-3.5" />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content align="end" sideOffset={4} className={menu}>
                    <DropdownMenu.Item
                      className={menuItem}
                      onSelect={() => setEditing(isEditing ? null : w.id)}
                    >
                      <Pencil className="size-3.5" />
                      {isEditing ? t('management.done') : t('management.editUser')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      className={menuItem}
                      onSelect={() => {
                        setTransferring(isTransferring ? null : w.id);
                        setTransferEmail('');
                      }}
                    >
                      <ArrowRightLeft className="size-3.5" />
                      {t('management.workspaces.transferOwner')}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      className={cn(menuItem, 'text-danger')}
                      onSelect={() => setConfirmDelete(w)}
                    >
                      <Trash2 className="size-3.5" />
                      {t('management.workspaces.delete')}
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </div>
            {isTransferring && (
              <div className="flex items-center gap-2 pl-1">
                <input
                  type="email"
                  autoFocus
                  aria-label={t('sync.email')}
                  placeholder={t('sync.email')}
                  value={transferEmail}
                  onChange={(e) => setTransferEmail(e.target.value)}
                  className="min-w-0 flex-1 rounded-md border border-border bg-bg px-1.5 py-0.5 text-sm"
                />
                <button
                  type="button"
                  disabled={busy === w.id || !transferEmail.trim()}
                  onClick={() => void transferOwner(w)}
                  className={cn(secondary, 'shrink-0')}
                >
                  {t('management.workspaces.transferButton')}
                </button>
              </div>
            )}
          </div>
        );
      })}

      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={t('management.workspaces.deleteConfirmTitle')}
        description={t('management.workspaces.deleteConfirmBody', {
          name: confirmDelete?.name ?? '',
        })}
        confirmLabel={t('management.workspaces.delete')}
        cancelLabel={t('app.cancel')}
        onConfirm={() => confirmDelete && void deleteWorkspace(confirmDelete)}
      />
    </div>
  );
}
